import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import { useSystemStore } from '@/client/store/useSystemStore'
import { API } from '@/client/utils/api'
import {
  DeleteProfileKeywordSettingResponse,
  GetProfileKeywordSettingsResponse,
  PostProfileKeywordSettingResponse,
  ProfileKeywordSetting,
  PutProfileKeywordSettingResponse
} from '@/typings/profile_keyword_settings'

type CreateProfileKeywordPayload = {
  name: string
  positive_keyword: string[]
  negative_keyword: string[]
  is_active: boolean
}

type UpdateProfileKeywordPayload = {
  keyword_setting_id: number
  name?: string
  positive_keyword?: string[]
  negative_keyword?: string[]
  is_active?: boolean
}

type ProfileKeywordSettingState = {
  api_profile_keyword_settings: ProfileKeywordSetting[]
}

export const useProfileKeywordSettingStore = defineStore('profileKeywordSettingStore', () => {
  const systemStore = useSystemStore()
  const api_profile_keyword_settings = ref<ProfileKeywordSettingState['api_profile_keyword_settings']>([])

  const fetchProfileKeywords = async (): Promise<void> => {
    const groupId = systemStore.requireCurrentGroupId()

    const response: GetProfileKeywordSettingsResponse = await API.get('v1/profile_keyword_settings', {
      params: {
        group_id: groupId
      }
    })

    if (response && response.data) {
      api_profile_keyword_settings.value = response.data
    }
  }

  const createProfileKeyword = async (
    payload: CreateProfileKeywordPayload
  ): Promise<PostProfileKeywordSettingResponse> => {
    const groupId = systemStore.requireCurrentGroupId()

    const params = {
      group_id: groupId,
      ...payload
    }

    const response: PostProfileKeywordSettingResponse = await API.post('v1/profile_keyword_settings', params)

    if (response && response.data) {
      await fetchProfileKeywords()
    }

    return response
  }

  const updateProfileKeyword = async (
    payload: UpdateProfileKeywordPayload
  ): Promise<PutProfileKeywordSettingResponse> => {
    const groupId = systemStore.requireCurrentGroupId()

    const params = {
      group_id: groupId,
      name: payload.name,
      positive_keyword: payload.positive_keyword,
      negative_keyword: payload.negative_keyword,
      is_active: payload.is_active
    }

    const response: PutProfileKeywordSettingResponse = await API.put(
      `v1/profile_keyword_settings/${payload.keyword_setting_id}`,
      params
    )

    if (response && response.data) {
      await fetchProfileKeywords()
    }

    return response
  }

  const deleteProfileKeyword = async (payload: number): Promise<DeleteProfileKeywordSettingResponse> => {
    const response: DeleteProfileKeywordSettingResponse = await API.delete(`v1/profile_keyword_settings/${payload}`)

    if (response && response.data) {
      await fetchProfileKeywords()
    }

    return response
  }

  return {
    api_profile_keyword_settings: readonly(api_profile_keyword_settings),
    fetchProfileKeywords,
    createProfileKeyword,
    updateProfileKeyword,
    deleteProfileKeyword
  }
})
